
/**
 * Drag & Drop component.
 *
 * How to use it:
 *
 *     <div>
 *        {{ render_data_table(table) }}
 *     </div>
 *
 *     <script>
 *       {% set tableName = table.name|preg_replace('/[^\\w]+/','') %}
 *       const el = '#grid_{{ tableName }} tbody';
 *       const rows = '#grid_{{ tableName }} tbody > tr';
 *       const grab = '.icon_grab';
 *       const action = (currentElementName, previousElementName) => {
 *          $.ajax({
 *            url: '/url',
 *            type: 'post',
 *            data: {
 *              current_name: currentElementName,
 *              previous_name: previousElementName
 *            },
 *            success() {
 *              window.location.reload(true);
 *            },
 *          });
 *       };
 *       const enable = true;
 *
 *       new DragAndDrop(el, rows, grab, action, enable);
 *     </script>
 *
 * Attributes:
 *
 * * `el` _{HTMLElement/String}_ Selector or element of draggable element. Eg. const el = 'table tbody'.
 * * `rows` _{HTMLElement/String}_ Selector or element of rows in draggable element. Eg. const rows = 'table tbody > tr'.
 * * `rows` _{String}_ Selector of grab icon element. Eg. const grab = '.grab icon'.
 * * `action` _{Function}_ Function run after drag ends. Attributes: `currentElementName` _{String}_, `previousElementName` _{String}_
 * * `enable` _{Boolean}_ Sets Drag & Drop visibility.
 */

import Sortable from 'sortablejs';

export default class DragAndDrop {

  constructor() {
    this.grab = '.icon_grab';

    const wrappers = document.querySelectorAll('.drag-drop-wrapper');
    if (wrappers.length) {
      wrappers.forEach(wrapper => {
        const table = wrapper.querySelector('table');
        const tbody = table && table.querySelector('tbody');
        if (tbody) {
          let enable = wrapper.getAttribute('data-enable') || true;
          const requestURL = wrapper.getAttribute('data-url');

          if (typeof enable === 'string') {
            enable = enable === 'true';
          }

          if (requestURL && enable) {
            this.enableDragIcon(tbody);
            this.init(tbody, requestURL);
          } else {
            this.disableDragIcon(tbody);
          }
        }
      });
    }
  }

  /**
   * Init Drag & Drop elements.
   *
   * @return {void}
   * @private
   */
  init(tableBody, requestURL) {
    Sortable.create(tableBody, {
      draggable: 'tr',
      handle: this.grab,
      onStart: this.onStart.bind(this, tableBody),
      onEnd: this.onEnd.bind(this, { tableBody, requestURL }),
    });
  }

  action(currentName, previousName, url) {
    $.ajax({
      url,
      type: "post",
        data: {
        current_name: currentName,
        previous_name: previousName
      },
      success() {

      },
    });
  }

  /**
   * Enable Drag & Drop
   *
   * @return {void}
   * @private
   */
  enableDragIcon(tableBody) {
    const grabIcons = tableBody.querySelectorAll(this.grab);
    grabIcons.forEach(grab => {

      if (grab.classList.contains('no-drop')) {
        grab.classList.remove('no-drop');
      }

      if (grab.hasAttribute('title')) {
        grab.removeAttribute('title');
      }

      const icon = grab.querySelector('i');
      if (icon && icon.classList.contains('fa-ban')) {
        icon.classList.remove('fa-ban');
        icon.classList.add('fa-ellipsis-v');
      }
    })
  }

  /**
   * Disabled Drag & Drop
   *
   * @return {void}
   * @private
   */
  disableDragIcon(tableBody) {
    const grabIcons = tableBody.querySelectorAll(this.grab);
    grabIcons.forEach(grab => {
      grab.classList.add('no-drop');
      grab.setAttribute('title', 'Sort the table by rank in ascending order to use drag&drop');

      const icon = grab.querySelector('i');
      if (icon && icon.classList.contains('fa-ellipsis-v')) {
        icon.classList.remove('fa-ellipsis-v');
        icon.classList.add('fa-ban')
      }
    });
  }

  /**
   * On Drag event start callback.
   *
   * @return {void}
   * @private
   */
  onStart(tableBody) {
    tableBody.classList.add('grabbing');
  }

  /**
   * On Drag event end callback.
   *
   * @param {Object} evt
   * @return {void}
   * @private
   */
  onEnd({ tableBody, requestURL }, evt) {
    const me = this;
    tableBody.classList.remove('grabbing');
    if (evt.newIndex !== evt.oldIndex) {
      const currentNameColumn = evt.item.querySelector('.name');
      if (!currentNameColumn) {
        return;
      }
      const currentName = currentNameColumn.innerText;
      const previousElement = evt.to.children[evt.newIndex].previousElementSibling;
      let previousElementName;
      if (previousElement && previousElement.nodeName === 'TR') {
        const nameColumn = previousElement.querySelector('.name');
        if (!nameColumn) {
          return;
        }
        previousElementName = nameColumn.innerText;
      } else {
        previousElementName = null;
      }

      if (me.action) {
        me.action(currentName, previousElementName, requestURL);
      }
    }
  }
}
